/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import useSWR from 'swr';
import qs from 'qs';

import request from '@/utils/request';
import type * as Type from '@/common/interface';

const buildAIMerchantListURL = (params: Type.AIMerchantListReq) =>
  `/answer/api/v1/ai-merchants?${qs.stringify(params, {
    skipNulls: true,
  })}`;

export const useAIMerchantOverview = (params: Type.AIMerchantListReq) => {
  const apiUrl = `/answer/api/v1/ai-merchants/overview?${qs.stringify(params, {
    skipNulls: true,
  })}`;
  const { data, error, mutate } = useSWR<Type.AIMerchantOverviewResp, Error>(
    [apiUrl],
    request.instance.get,
  );
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const fetchAIMerchantList = (params: Type.AIMerchantListReq) => {
  return request.get<Type.ListResult<Type.AIMerchantItem>>(
    buildAIMerchantListURL(params),
  );
};

export const useAIMerchantList = (params: Type.AIMerchantListReq) => {
  const apiUrl = buildAIMerchantListURL(params);
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.AIMerchantItem>,
    Error
  >([apiUrl], request.instance.get);
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const useAIMerchantDetail = (id?: number) => {
  const apiUrl = id ? `/answer/api/v1/ai-merchants/detail?id=${id}` : null;
  const { data, error, mutate } = useSWR<Type.AIMerchantDetailResp, Error>(
    apiUrl ? [apiUrl] : null,
    request.instance.get,
  );
  return {
    data,
    isLoading: Boolean(id) && !data && !error,
    error,
    mutate,
  };
};

export const useAIMerchantProducts = (
  merchantID?: number,
  page = 1,
  pageSize = 24,
) => {
  const apiUrl = merchantID
    ? `/answer/api/v1/ai-merchants/products?${qs.stringify(
        {
          merchant_id: merchantID,
          page,
          page_size: pageSize,
        },
        { skipNulls: true },
      )}`
    : null;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.AIMerchantProductItem>,
    Error
  >(apiUrl ? [apiUrl] : null, request.instance.get);
  return {
    data,
    isLoading: Boolean(merchantID) && !data && !error,
    error,
    mutate,
  };
};

export const useAIMerchantReviews = (
  params: Type.AIMerchantReviewListReq | null,
) => {
  const apiUrl = params
    ? `/answer/api/v1/ai-merchants/reviews?${qs.stringify(params, {
        skipNulls: true,
      })}`
    : null;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.AIMerchantReviewItem>,
    Error
  >(apiUrl ? [apiUrl] : null, request.instance.get);
  return {
    data,
    isLoading: Boolean(params) && !data && !error,
    error,
    mutate,
  };
};

export const submitAIMerchantReview = (
  params: Type.AIMerchantReviewCreateReq,
) => {
  return request.post<Type.AIMerchantReviewItem>(
    '/answer/api/v1/ai-merchants/reviews',
    params,
  );
};

export const createAIMerchantApplication = (
  params: Type.AIMerchantApplicationCreateReq,
) => {
  return request.post<Type.AIMerchantApplicationItem>(
    '/answer/api/v1/ai-merchants/applications',
    params,
  );
};

export const useCurrentAIMerchantReviewerProfile = (enabled: boolean) => {
  const apiUrl = enabled
    ? '/answer/api/v1/ai-merchants/reviewer-profile'
    : null;
  const { data, error, mutate } = useSWR<
    Type.AIMerchantReviewerProfileItem,
    Error
  >(apiUrl ? [apiUrl] : null, request.instance.get);
  return {
    data,
    isLoading: enabled && !data && !error,
    error,
    mutate,
  };
};

export const createAIMerchantConversation = (merchantID: number) => {
  return request.post<Type.AIMerchantConversationItem>(
    '/answer/api/v1/ai-merchants/conversations',
    { merchant_id: merchantID },
  );
};

export const useAIMerchantMessages = (
  conversationID?: number,
  afterID?: number,
) => {
  const apiUrl = conversationID
    ? `/answer/api/v1/ai-merchants/messages?${qs.stringify({
        conversation_id: conversationID,
        after_id: afterID || undefined,
        page_size: 100,
      })}`
    : null;
  const { data, error, mutate } = useSWR<Type.AIMerchantMessageItem[], Error>(
    apiUrl ? [apiUrl] : null,
    request.instance.get,
    { refreshInterval: conversationID ? 5000 : 0 },
  );
  return {
    data,
    isLoading: Boolean(conversationID) && !data && !error,
    error,
    mutate,
  };
};

export const sendAIMerchantMessage = (
  conversationID: number,
  content: string,
) => {
  return request.post<Type.AIMerchantMessageItem>(
    '/answer/api/v1/ai-merchants/messages',
    {
      conversation_id: conversationID,
      content,
    },
  );
};
